import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ChevronDown, FileSpreadsheet, Upload } from 'lucide-react';
import axios from 'axios';
import StockUploadBtn from './StockUploadBtn';

const API = process.env.REACT_APP_BACKEND_URL;

// CRR Report Panel - shows material availability for a project inside Reports
function CRRReportPanel({ projectId }) {
  const [crr, setCrr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [setQty, setSetQty] = useState(1);
  const [expandedItem, setExpandedItem] = useState(null);
  const [search, setSearch] = useState('');
  const fileRef = React.useRef(null);

  const fetchCrr = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/planning/projects/${projectId}/crr?set_qty=${setQty}`);
      setCrr(res.data);
    } catch { }
    setLoading(false);
  }, [projectId, setQty]);

  useEffect(() => { fetchCrr(); }, [fetchCrr]);

  // Refresh when stock is uploaded
  useEffect(() => {
    const handler = () => fetchCrr();
    window.addEventListener('stock-updated', handler);
    return () => window.removeEventListener('stock-updated', handler);
  }, [fetchCrr]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/planning/projects/${projectId}/crr/upload`, fd);
      toast.success('CRR updated');
      if (res.data.parse_warning) toast.warning(res.data.parse_warning, { duration: 12000 });
      fetchCrr();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  if (loading) return null;
  if (!crr || !crr.total_items) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
        <FileSpreadsheet className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <div className="text-sm text-slate-500 mb-3">No CRR data uploaded for this project</div>
        <label className="cursor-pointer inline-flex items-center gap-1.5 px-4 py-2 bg-cyan-600 text-white rounded-lg text-xs font-semibold hover:bg-cyan-700">
          <Upload className="w-3.5 h-3.5" /> Upload CRR
          <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={handleUpload} />
        </label>
      </div>
    );
  }

  const s = crr.summary || {};
  const items = crr.items || [];
  const groups = Object.entries(crr.group_summary || {}).sort((a, b) => b[1].short - a[1].short);

  let filtered = items;
  if (filter === 'short') filtered = items.filter(i => (i.total_short || 0) > 0);
  else if (filter === 'available') filtered = items.filter(i => (i.total_stock || 0) >= (i.qty_required || 0) && (i.qty_required || 0) > 0);
  else if (filter === 'no-stock') filtered = items.filter(i => (i.total_stock || 0) === 0 && (i.qty_required || 0) > 0);
  if (groupFilter !== 'all') filtered = filtered.filter(i => (i.group || 'Unassigned') === groupFilter);
  if (vendorFilter !== 'all') filtered = filtered.filter(i => (i.vendor || 'Unassigned') === vendorFilter);
  if (search) { const q = search.toLowerCase(); filtered = filtered.filter(i => (i.item_code||'').toLowerCase().includes(q)||(i.description||'').toLowerCase().includes(q)||(i.vendor||'').toLowerCase().includes(q)||(i.group||'').toLowerCase().includes(q)); }

  const totalShortQty = items.filter(i => (i.total_short || 0) > 0).reduce((sum, i) => sum + (i.total_short || 0), 0);

  // Build vendor summary for filter pills
  const vendorSummary = {};
  items.forEach(i => {
    const v = i.vendor || 'Unassigned';
    if (!vendorSummary[v]) vendorSummary[v] = { total: 0, short: 0 };
    vendorSummary[v].total++;
    if ((i.total_short || 0) > 0) vendorSummary[v].short++;
  });
  const vendors = Object.entries(vendorSummary).sort((a, b) => b[1].total - a[1].total);

  const handleRealStock = async (itemCode, val) => {
    try {
      await axios.put(`${API}/api/stock/real-stock`, { item_code: itemCode, qty: val === '' ? '' : parseFloat(val) });
      fetchCrr();
    } catch { }
  };

  return (
    <div className="bg-white border border-cyan-200 rounded-xl overflow-hidden" data-testid="crr-report-panel">
      <div className="bg-cyan-50 px-4 py-2.5 border-b border-cyan-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-cyan-600" />
          <span className="text-xs font-bold text-cyan-800">Material Availability (CRR)</span>
          <span className="text-[10px] text-cyan-500">{crr.total_items} items | Bill: {crr.bill_number}</span>
          <div className="flex items-center gap-1.5 ml-2 bg-white border border-cyan-300 rounded-lg px-2 py-1">
            <span className="text-[10px] font-semibold text-cyan-700">Sets:</span>
            <input type="number" min={1} value={setQty} onChange={e => { const v = parseInt(e.target.value); if (v > 0) setSetQty(v); }}
              className="w-12 text-center border-0 text-sm font-bold text-cyan-700 focus:outline-none bg-transparent"
              data-testid="crr-set-qty" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-slate-400">CRR: {crr.uploaded_at ? new Date(crr.uploaded_at).toLocaleDateString() : '-'}</span>
          {crr.stock_info?.ksu_uploaded && <span className="text-[9px] text-indigo-400">KSU: {new Date(crr.stock_info.ksu_uploaded).toLocaleDateString()}</span>}
          {crr.stock_info?.kiel_uploaded && <span className="text-[9px] text-orange-400">Kiel: {new Date(crr.stock_info.kiel_uploaded).toLocaleDateString()}</span>}
          <label className="cursor-pointer flex items-center gap-1 px-2 py-1 bg-cyan-600 text-white rounded text-[10px] font-semibold hover:bg-cyan-700">
            <Upload className="w-3 h-3" /> {uploading ? '...' : 'Update CRR'}
            <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
          <StockUploadBtn location="ksu" label="KSU Stock" color="indigo" />
          <StockUploadBtn location="kiel" label="Kiel Stock" color="orange" />
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-5 gap-2 p-3">
        {[
          { key: 'all', label: 'Total Items', value: s.total, color: 'slate' },
          { key: 'available', label: 'Available', value: s.available, color: 'emerald' },
          { key: 'short', label: 'Short (Total)', value: s.short, color: 'red', sub: totalShortQty > 0 ? `${totalShortQty} pcs` : '' },
          { key: 'no-stock', label: 'No Stock', value: s.no_stock, color: 'orange' },
          { key: 'coverage', label: 'Coverage', value: s.total > 0 ? `${Math.round(((s.available || 0) / s.total) * 100)}%` : '-', color: (s.available || 0) >= s.total ? 'emerald' : 'amber', isText: true },
        ].map(c => (
          <button key={c.key} onClick={() => !c.isText && setFilter(c.key)}
            className={`text-center p-2.5 rounded-lg border transition-all ${!c.isText && filter === c.key ? `bg-${c.color}-50 border-${c.color}-300 ring-1 ring-${c.color}-200` : 'bg-white border-slate-200 hover:border-slate-300'} ${c.isText ? 'cursor-default' : 'cursor-pointer'}`}>
            <div className={`text-xl font-black text-${c.color}-600`}>{c.value || 0}</div>
            <div className={`text-[9px] text-${c.color}-500 font-semibold uppercase`}>{c.label}</div>
            {c.sub && <div className="text-[8px] text-red-400 mt-0.5">{c.sub}</div>}
          </button>
        ))}
      </div>

      {/* Filters: Group + Vendor + Search */}
      <div className="px-3 pb-2 flex items-center gap-2">
        <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
          className="border border-slate-200 rounded-lg px-2 py-1.5 text-[10px] font-semibold focus:ring-cyan-400 focus:outline-none min-w-[140px]">
          <option value="all">All Groups ({items.length})</option>
          {groups.map(([g, gs]) => (
            <option key={g} value={g}>{g}: {gs.total}{gs.short > 0 ? ` (${gs.short} short)` : ''}</option>
          ))}
        </select>
        <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
          className="border border-slate-200 rounded-lg px-2 py-1.5 text-[10px] font-semibold focus:ring-violet-400 focus:outline-none min-w-[160px]">
          <option value="all">All Vendors</option>
          {vendors.map(([v, vs]) => (
            <option key={v} value={v}>{v}: {vs.total}{vs.short > 0 ? ` (${vs.short} short)` : ''}</option>
          ))}
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search code, description, vendor..."
          className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:ring-cyan-400 focus:outline-none focus:border-cyan-300" data-testid="crr-search" />
        {(groupFilter !== 'all' || vendorFilter !== 'all' || search) && (
          <button onClick={() => { setGroupFilter('all'); setVendorFilter('all'); setSearch(''); }}
            className="text-[10px] font-semibold text-slate-400 hover:text-red-500 px-2">Clear</button>
        )}
        <span className="text-[10px] text-slate-400 whitespace-nowrap">{filtered.length} items</span>
      </div>

      {/* Items Table */}
      <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
        <table className="w-full text-[10px]">
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr className="text-slate-400 uppercase tracking-wider text-[8px]">
              <th className="px-2 py-1.5 text-left font-semibold">#</th>
              <th className="px-2 py-1.5 text-left font-semibold">Item Code</th>
              <th className="px-2 py-1.5 text-left font-semibold">Description</th>
              <th className="px-2 py-1.5 text-left font-semibold">Group</th>
              <th className="px-2 py-1.5 text-left font-semibold">Vendor</th>
              <th className="px-2 py-1.5 text-right font-semibold">Per Bill</th>
              <th className="px-2 py-1.5 text-right font-semibold">Required</th>
              <th className="px-2 py-1.5 text-right font-semibold text-indigo-500">KSU Stock</th>
              <th className="px-2 py-1.5 text-right font-semibold text-orange-500">Kiel Stock</th>
              <th className="px-2 py-1.5 text-right font-semibold text-violet-500">Real Stock</th>
              <th className="px-2 py-1.5 text-right font-semibold">Total</th>
              <th className="px-2 py-1.5 text-right font-semibold text-indigo-400">KSU Short</th>
              <th className="px-2 py-1.5 text-right font-semibold text-orange-400">Kiel Short</th>
              <th className="px-2 py-1.5 text-right font-semibold text-red-400">Total Short</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item, i) => {
              const isShort = (item.total_short || 0) > 0;
              const noStock = (item.total_stock || 0) === 0 && item.qty_required > 0;
              return (
                <React.Fragment key={item.item_code}>
                <tr className={`${isShort ? 'bg-red-50/50' : noStock ? 'bg-orange-50/50' : i % 2 ? 'bg-slate-50/30' : ''} hover:bg-blue-50/30 cursor-pointer`}
                  onClick={() => setExpandedItem(expandedItem === item.item_code ? null : item.item_code)}>
                  <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
                  <td className="px-2 py-1.5 font-mono font-semibold text-slate-700 flex items-center gap-1">
                    <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${expandedItem === item.item_code ? 'rotate-180' : ''}`} />
                    {item.item_code}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 max-w-[180px] truncate" title={item.description}>{item.description}</td>
                  <td className="px-2 py-1.5 text-slate-500">{item.group || '-'}</td>
                  <td className="px-2 py-1.5 text-slate-500 max-w-[120px] truncate" title={item.vendor}>{item.vendor || '-'}</td>
                  <td className="px-2 py-1.5 text-right text-slate-600">{item.qty_per_bill}</td>
                  <td className="px-2 py-1.5 text-right text-blue-600 font-semibold">{item.qty_required}</td>
                  <td className="px-2 py-1.5 text-right text-indigo-600">{item.ksu_available || 0}</td>
                  <td className="px-2 py-1.5 text-right text-orange-600">{item.kiel_available || 0}</td>
                  <td className="px-2 py-1.5 text-right" onClick={e => e.stopPropagation()}>
                    <input type="number" defaultValue={item.real_stock ?? ''}
                      className="w-16 text-right border border-violet-200 rounded px-1 py-0.5 text-[10px] text-violet-700 focus:ring-1 focus:ring-violet-400 focus:outline-none"
                      placeholder="-"
                      onBlur={e => handleRealStock(item.item_code, e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
                  </td>
                  <td className={`px-2 py-1.5 text-right font-bold ${noStock ? 'text-orange-600' : 'text-slate-700'}`}>{item.total_stock || 0}</td>
                  <td className={`px-2 py-1.5 text-right ${(item.ksu_short || 0) > 0 ? 'text-indigo-500 font-semibold' : 'text-slate-300'}`}>{item.ksu_short || 0}</td>
                  <td className={`px-2 py-1.5 text-right ${(item.kiel_short || 0) > 0 ? 'text-orange-500 font-semibold' : 'text-slate-300'}`}>{item.kiel_short || 0}</td>
                  <td className={`px-2 py-1.5 text-right font-bold ${isShort ? 'text-red-600' : 'text-slate-300'}`}>{item.total_short || 0}</td>
                </tr>
                {expandedItem === item.item_code && (
                  <tr className="bg-slate-50 border-l-4 border-cyan-400">
                    <td colSpan={14} className="px-4 py-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                          <div className="text-[10px] font-bold text-indigo-700 uppercase mb-2">KSU Stock Detail</div>
                          <div className="grid grid-cols-4 gap-3 text-center">
                            <div><div className="text-[9px] text-slate-400">On Hand</div><div className="text-sm font-bold text-indigo-700">{item.ksu_on_hand || 0}</div></div>
                            <div><div className="text-[9px] text-slate-400">On PO</div><div className={`text-sm font-bold ${(item.ksu_on_po || 0) > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{item.ksu_on_po || 0}</div></div>
                            <div><div className="text-[9px] text-slate-400">On SO</div><div className={`text-sm font-bold ${(item.ksu_on_so || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{item.ksu_on_so || 0}</div></div>
                            <div><div className="text-[9px] text-slate-400">Available</div><div className="text-sm font-bold text-indigo-600">{item.ksu_available || 0}</div></div>
                          </div>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                          <div className="text-[10px] font-bold text-orange-700 uppercase mb-2">Kiel Stock Detail</div>
                          <div className="grid grid-cols-4 gap-3 text-center">
                            <div><div className="text-[9px] text-slate-400">On Hand</div><div className="text-sm font-bold text-orange-700">{item.kiel_on_hand || 0}</div></div>
                            <div><div className="text-[9px] text-slate-400">On PO</div><div className={`text-sm font-bold ${(item.kiel_on_po || 0) > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{item.kiel_on_po || 0}</div></div>
                            <div><div className="text-[9px] text-slate-400">On SO</div><div className={`text-sm font-bold ${(item.kiel_on_so || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{item.kiel_on_so || 0}</div></div>
                            <div><div className="text-[9px] text-slate-400">Available</div><div className="text-sm font-bold text-orange-600">{item.kiel_available || 0}</div></div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={14} className="px-3 py-6 text-center text-slate-400">No items match this filter</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default CRRReportPanel;
